import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button, Form, Icon, Input, message, Modal, Spin, Upload } from 'antd';
import saveDebtsFile from '../../../store/thunks/saveDebtsFile';
import { useDispatch, useSelector } from 'react-redux';
import getFileTemplate from '../../../requests/getFileTemplate';
import { useHistory, useLocation } from 'react-router-dom';
import { setAlertUrl, setVisibleModal } from '../../../store/actions/actions';
import styles from './UploadDebtsFile.module.scss';
import Footer from './Footer/Footer';
import useFilesQuery from '../../../hooks/useFilesQuery';
import getDebtsList from '../../../store/thunks/getDebtsList';

const isRightFormat = (file) => {
  return (
    file?.name.split('.').pop() === 'xlsx' ||
    file?.name.split('.').pop() === 'csv' ||
    file?.type ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
};

const FormNode = forwardRef(({ form }, ref) => {
  ref.current = form;

  const { getFieldDecorator } = form;

  return (
    <Form>
      <Form.Item label="Наименование">
        {getFieldDecorator('loading_name', {
          rules: [
            {
              max: 100,
              message: 'Наименование не должно превышать 100 символов',
            },
            {
              required: true,
              message: 'Пожалуйста, введите наименование',
            },
          ],
        })(<Input placeholder="Наименование загрузки" />)}
      </Form.Item>
    </Form>
  );
});

const FormDebts = Form.create('form-debts')(FormNode);

const UploadDebtsFile = memo(() => {
  const history = useHistory();
  const location = useLocation();

  const dispatch = useDispatch();

  const [file, setFile] = useState(null);
  const [fileList, setFileList] = useState(null);
  const [loading, setLoading] = useState(false);

  const { refetch } = useFilesQuery();

  const params = new URLSearchParams(location.search);
  const companyIdFromUrl = params.get('companyId');

  const formRef = useRef();

  const visibleModal = useSelector((state) => state.debtsStore.visibleModal);
  const currentCompany = useSelector(
    (store) => store.debtsStore.currentCompany
  );

  const { visible, value } = visibleModal || {};

  const downloadTemplate = useCallback(async () => {
    setLoading(true);
    await getFileTemplate();
    setLoading(false);
  }, []);

  const openModal = useCallback(
    () => dispatch(setVisibleModal({ visible: true })),
    []
  );

  const closeModal = useCallback(() => {
    if (!loading) {
      dispatch(setVisibleModal({ visible: false }));
    }
  }, [loading, dispatch]);

  const onUpload = async () => {
    if (!value && !formRef.current) return;

    if (!file) {
      message.error(value ? 'Выберите новый файл' : 'Выберите файл');
      return;
    }

    if (value) {
      try {
        setLoading(true);

        const payload = {
          file,
          serviceCompanyId: currentCompany?.id,
          fileId: value?.fileId,
          dispatch,
        };

        await saveDebtsFile(payload);

        dispatch(setVisibleModal({ visible: false }));
        refetch();

        dispatch(getDebtsList({ fileId: value?.fileId }));

        history.push({
          pathname: `/Debts/file/${value?.fileId}`,
          search: `?companyId=${companyIdFromUrl}`,
        });
      } finally {
        setLoading(false);
      }

      return;
    }

    formRef.current.validateFields(async (err, values) => {
      if (!value && err) return;

      if (!file) {
        message.error('Выберите файл');
        return;
      }

      try {
        setLoading(true);

        const payload = {
          fileLoadingName: values.loading_name,
          file,
          serviceCompanyId: currentCompany?.id,
          dispatch,
        };

        const resultSave = await saveDebtsFile(payload);
        console.log(resultSave, 'resultSave');

        dispatch(setVisibleModal({ visible: false }));

        const result = await refetch();

        const freshData = result.data;

        if (freshData?.[0]?.fileId && resultSave !== 'Error') {
          history.push({
            pathname: `/Debts/file/${freshData[0].fileId}`,
            search: `?companyId=${companyIdFromUrl}`,
          });

          return;
        }

        history.push({
          pathname: `/Debts`,
          search: `?companyId=${companyIdFromUrl}`,
        });
      } finally {
        setLoading(false);
      }
    });
  };

  const onFileListChange = useCallback((info) => {
    if (isRightFormat(info.file)) {
      let fileList = [...info.fileList];
      fileList = fileList.slice(-1);
      setFileList(fileList);
    } else setFileList([]);
  }, []);

  const beforeUpload = useCallback((file) => {
    if (isRightFormat(file)) {
      setFile(file);
    } else message.error('У файла должно быть расширение xlsx или csv');
    return false;
  }, []);

  useEffect(() => {
    if (!visible) {
      setFileList();
      setFile();
    }
    if (visible && Boolean(value)) {
      setFile();
    }
  }, [visible, value]);

  useEffect(() => {
    if (visible) {
      dispatch(setAlertUrl(null));
    }
  }, [dispatch, visible]);

  return (
    <>
      <Button
        className="primary_button primary_button-circled button"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={openModal}
      >
        Загрузить файл
        <Icon type="plus" />
      </Button>
      {visible && (
        <Modal
          onCancel={closeModal}
          visible={visible}
          title={
            Boolean(value)
              ? 'Обновить файл дебиторской задолженности'
              : 'Загрузить файл дебиторской задолженности'
          }
          footer={
            <Footer
              value={value}
              onUpload={onUpload}
              onCancel={closeModal}
            />
          }
        >
          <Spin spinning={loading}>
            {Boolean(value) ? (
              <div className={styles.blockName}>
                <div className={styles.title}>
                  Наименование обновляемой загрузки:
                </div>
                <div className={styles.name}>{value?.fileLoadingName}</div>
              </div>
            ) : (
              <FormDebts wrappedComponentRef={formRef} />
            )}

            <Upload.Dragger
              onChange={onFileListChange}
              fileList={fileList}
              beforeUpload={beforeUpload}
            >
              <Icon
                type="file-add"
                theme="outlined"
              />{' '}
              {Boolean(value) ? 'выбрать новый файл' : 'выбрать файл'}
            </Upload.Dragger>
            <div className="file_hint">
              Файл должен быть расширения XLSX или CSV
            </div>
            <Button
              type="link"
              className="file_hint"
              onClick={downloadTemplate}
            >
              Скачать шаблон для загрузки файла
            </Button>
          </Spin>
        </Modal>
      )}
    </>
  );
});

export default UploadDebtsFile;
